/* ============================================================================
   CLIENTES — el catálogo de cada empresa, y de quién es cada vuelo.

   POR QUÉ EXISTE

   El informe de horas tiene cuatro cortes, y dos de ellos —por empresa y por
   cliente— salen vacíos por la misma razón: **el asiento del libro no dice para
   quién se voló**. Un informe sobre columnas que no existen sale vacío, y un
   informe vacío parece un informe. Eso fue lo que la entrega 137 dejó dicho en
   pantalla; esto es lo que falta para que deje de estar vacío.

   ═══ EL CLIENTE ES DE UNA EMPRESA, NO DE LA PLATAFORMA ═══

   No hay una lista global de clientes. Cada empresa tiene los suyos, y elegir un
   cliente **determina de qué empresa es el vuelo**: es el mismo hecho dicho dos
   veces, y por eso se guarda una sola vez —en el cliente— y el vuelo puede
   heredarlo.

   Si el catálogo fuera global, dos empresas con un cliente llamado «YPF» serían
   el mismo renglón y las horas de una aparecerían en el informe de la otra.

   ═══ UN CATÁLOGO DE INFORME SE ORDENA DISTINTO QUE UNA AGENDA ═══

   Acá el nombre no es una etiqueta: es la **clave por la que se agrupan horas**.
   Dos renglones con el mismo nombre no son dos clientes: son las horas del mismo
   cliente partidas en dos, la mitad en cada uno, y ninguna de las dos parece
   mal. Por eso el nombre repetido dentro de una empresa se rechaza.

   Y por eso también la baja es LÓGICA: borrar un cliente que tiene vuelos
   cargados dejaría esos asientos apuntando a un id que ya no existe, y las horas
   pasarían a «(sin cliente)» sin que nadie hubiera tocado un vuelo. Un cliente
   dado de baja no se puede elegir para un vuelo nuevo, pero **sigue existiendo
   para el informe de lo que ya se voló**.

   ═══ QUÉ NO HACE ═══

   No guarda nada y no factura. Tarifas, condiciones y vencimientos son de la
   entrega de facturación —y para eso las horas tienen que estar bien primero—.
   Tampoco sabe de permisos finos: administrar el catálogo es parte de editar los
   datos de la empresa (`empresa.escribir`), y elegir el cliente de un vuelo es
   parte de registrarlo (`vuelos.escribir`). Ver la nota de `revisarOperacionDelVuelo`.
   ========================================================================== */
import { puede } from "./identidad.js";

const lista = (v) => (Array.isArray(v) ? v : []);

/* ─────────────────────────────────────────────────────────────────────────────
 * LOS RECHAZOS, EN PALABRAS
 *
 * Mismo criterio que en identidad.js: la pantalla muestra la frase tal cual. Un
 * «no se pudo» obliga a adivinar el motivo; estos dicen qué pasó y, donde se
 * puede, por qué importa.
 * ──────────────────────────────────────────────────────────────────────────── */
export const MOTIVO_CLIENTE = {
  SIN_NOMBRE: "Falta el nombre del cliente.",
  SIN_EMPRESA: "Falta la empresa: los clientes son de una empresa, no de la plataforma.",
  EMPRESA_INEXISTENTE: "La empresa no existe.",
  EMPRESA_DE_BAJA: "La empresa está dada de baja: no admite clientes nuevos.",
  SIN_PERMISO: "Tu perfil no edita los datos de la empresa.",
  NOMBRE_REPETIDO: "Ya hay un cliente con ese nombre en esta empresa. Con dos renglones iguales, las horas se reparten entre los dos y el informe muestra la mitad en cada uno.",
  YA_DE_BAJA: "Ese cliente ya está dado de baja.",
  YA_ACTIVO: "Ese cliente ya está activo.",

  /* Los del vuelo: son los que evitan que una hora se cuente en la empresa que
     no la voló, que es el error que este catálogo existe para no repetir. */
  CLIENTE_INEXISTENTE: "El cliente no existe.",
  CLIENTE_DE_BAJA: "El cliente está dado de baja: no se le pueden cargar vuelos nuevos, pero sus horas ya voladas siguen contando.",
  EMPRESA_Y_CLIENTE_DISCREPAN: "La empresa del vuelo y la del cliente no son la misma. Si hay cliente, la empresa es la suya.",
};

/**
 * Cómo se comparan dos nombres de cliente.
 *
 * Sin acentos, sin mayúsculas y **sin signos de puntuación**, que acá no se
 * convierten en guiones como en el nombre corto de una empresa: se borran.
 *
 * La diferencia no es cosmética. Con guiones, «S.A.» da `s-a` y «SA» da `sa`, y
 * entonces «Minera Andes S.A.» y «Minera Andes SA» pasarían por dos clientes
 * distintos: dos renglones, las horas partidas en dos, y ninguna de las dos
 * filas parece mal. Borrando la puntuación son el mismo cliente, que es la
 * verdad.
 *
 * Lo que el criterio NO hace es adivinar: borra signos, no palabras. «Minera
 * Andes» y «Minera Andes S.A.» quedan distintos, y está bien que queden —decidir
 * que son el mismo exige adivinar, y adivinar mal acá junta las horas de dos
 * clientes que no son el mismo—.
 */
export function claveDeNombre(nombre) {
  return String(nombre ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

/* ─────────────────────────────────────────────────────────────────────────────
 * ESCRIBIR EL CATÁLOGO
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Un cliente nuevo.
 *
 * `id` propio y no el nombre: el nombre se puede corregir —una tilde, una razón
 * social completa— y los vuelos ya cargados tienen que seguir apuntando al mismo
 * cliente. Con el nombre como clave, corregirlo partiría la historia en dos.
 */
export function nuevoCliente({ nombre, empresaId, contacto = "", email = "", quien = null, ahora = new Date() } = {}) {
  return {
    /* Con sufijo al azar, igual que `nuevoUsuario` y a diferencia de
       `nuevaEmpresa`. Acá el motivo es concreto: un catálogo se puede cargar de
       a muchos —una lista de clientes pegada de una planilla— y dos altas en el
       MISMO milisegundo darían el mismo id. Dos clientes con el mismo id son un
       cliente con las horas de los dos, y el informe no lo delata. */
    id: `cli-${ahora.getTime().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    /* La empresa va PRIMERO en el objeto y no es decorativa: es a quién
       pertenece, y es lo que el aislamiento compara en cada consulta. */
    empresaId: empresaId ?? null,
    nombre: String(nombre ?? "").trim(),
    contacto: String(contacto ?? "").trim(),
    email: String(email ?? "").trim(),
    activo: true,
    historial: quien
      ? [{ el: ahora.toISOString(), quien, que: "alta", de: null, a: "activo" }]
      : [],
  };
}

/**
 * ¿Se puede guardar este cliente? `null` si sí; si no, el motivo.
 *
 * `editando` es el id que se está cambiando: sin eso, guardar sin tocar el
 * nombre choca contra sí mismo.
 */
export function revisarCliente(datos = {}, { clientes = [], empresas = [], editando = null, quien = null } = {}) {
  if (!puede(quien?.perfil, "empresa.escribir")) return MOTIVO_CLIENTE.SIN_PERMISO;

  const empresaId = datos.empresaId ?? null;
  if (!empresaId) return MOTIVO_CLIENTE.SIN_EMPRESA;
  const empresa = lista(empresas).find((e) => e.id === empresaId);
  if (!empresa) return MOTIVO_CLIENTE.EMPRESA_INEXISTENTE;
  /* Una empresa dada de baja no admite clientes nuevos. No es una regla de
     estilo: su gente ya no entra, así que nadie podría elegir el cliente. */
  if (empresa.activo === false) return MOTIVO_CLIENTE.EMPRESA_DE_BAJA;

  if (!String(datos.nombre ?? "").trim()) return MOTIVO_CLIENTE.SIN_NOMBRE;

  /* El choque se busca SOLO dentro de la empresa. El mismo nombre en dos
     empresas es legítimo —dos operadores pueden volar para el mismo cliente— y
     veces es lo normal. */
  const clave = claveDeNombre(datos.nombre);
  const choque = lista(clientes).find(
    (c) => c && c.empresaId === empresaId && c.id !== editando && claveDeNombre(c.nombre) === clave,
  );
  if (choque) return MOTIVO_CLIENTE.NOMBRE_REPETIDO;

  return null;
}

/** Cambia los datos de un cliente sin tocar su historial ni su estado. */
export function editarCliente(cliente, cambios = {}, { quien = null, ahora = new Date() } = {}) {
  if (!cliente) return cliente;
  const antes = { nombre: cliente.nombre, contacto: cliente.contacto, email: cliente.email };
  const despues = {
    nombre: cambios.nombre !== undefined ? String(cambios.nombre).trim() : cliente.nombre,
    contacto: cambios.contacto !== undefined ? String(cambios.contacto).trim() : cliente.contacto,
    email: cambios.email !== undefined ? String(cambios.email).trim() : cliente.email,
  };
  return {
    ...cliente,
    ...despues,
    historial: anotar(cliente.historial, { el: ahora.toISOString(), quien, que: "edicion", de: antes, a: despues }),
  };
}

/* ─────────────────────────────────────────────────────────────────────────────
 * LA BAJA
 *
 * Lógica, siempre. Un cliente que se borra de verdad deja sus vuelos apuntando
 * a un id inexistente: esas horas pasan a «(sin cliente)» y el informe de un mes
 * cerrado cambia solo. La baja dice «no se elige más», no «no existió».
 * ──────────────────────────────────────────────────────────────────────────── */
export function darDeBajaCliente(cliente, { quien = null, motivo = "", ahora = new Date() } = {}) {
  if (!cliente || cliente.activo === false) return cliente;
  return {
    ...cliente,
    activo: false,
    historial: anotar(cliente.historial, {
      el: ahora.toISOString(), quien, que: "baja", de: "activo", a: "baja", motivo,
    }),
  };
}

export function reactivarCliente(cliente, { quien = null, ahora = new Date() } = {}) {
  if (!cliente || cliente.activo) return cliente;
  return {
    ...cliente,
    activo: true,
    historial: anotar(cliente.historial, { el: ahora.toISOString(), quien, que: "reactivacion", de: "baja", a: "activo" }),
  };
}

/** El historial, sin que un cliente viejo —sin el arreglo— rompa la pantalla. */
function anotar(historial, movimiento) {
  return [...lista(historial), movimiento];
}

/* ─────────────────────────────────────────────────────────────────────────────
 * LEER EL CATÁLOGO
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Los clientes de UNA empresa.
 *
 * Es la regla de aislamiento, en una función, para que no la repita cada
 * pantalla con su propio filtro: es exactamente así —un `.filter` escrito de
 * nuevo, con el campo al revés— como se cuela un dato de otra empresa.
 *
 * `incluirBajas` existe para el caso que importa: **el informe**. Mirar las
 * horas de un cliente dado de baja es legítimo y necesario; lo que no se puede
 * es elegirlo para un vuelo nuevo.
 */
export function clientesDe(clientes = [], empresaId = null, { incluirBajas = false } = {}) {
  return lista(clientes).filter((c) => c && c.empresaId === empresaId && (incluirBajas || c.activo !== false));
}

/** El cliente, solo si es de esa empresa. Si es de otra, `null`: no hay
    «pertenece pero distinto». */
export function clienteDeEmpresa(clientes = [], clienteId = null, empresaId = null) {
  if (!clienteId) return null;
  return lista(clientes).find((c) => c && c.id === clienteId && c.empresaId === empresaId) || null;
}

/** El nombre, o `null` si el id no está en el catálogo.
 *
 * Devuelve `null` y no un texto inventado: quien llama decide qué hacer con «no
 * lo sé», y las dos situaciones no son la misma cosa. Un asiento que apunta a un
 * cliente que no está en el catálogo y un asiento que no fue para ningún cliente
 * son hechos distintos, y el informe los muestra distintos a propósito —al
 * primero se le ve el id crudo—: es la señal de que falta un renglón en el
 * catálogo, y taparla con «(sin cliente)» la convertiría en un vuelo que no fue
 * para nadie. */
export function nombreDeCliente(clientes = [], clienteId = null) {
  if (!clienteId) return null;
  return lista(clientes).find((c) => c && c.id === clienteId)?.nombre || null;
}

/* ─────────────────────────────────────────────────────────────────────────────
 * DE QUIÉN ES EL VUELO
 *
 * Estas dos funciones son el corazón de por qué este módulo existe. La primera
 * dice qué empresa corresponde; la segunda se niega a guardar un vuelo donde no
 * se sabe.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * La empresa que le corresponde a un vuelo.
 *
 * Con cliente, **es la del cliente**: no se pregunta. Sin cliente —un traslado
 * propio, una instrucción— hay que decirla igual, porque si no el vuelo cae en
 * «(sin empresa)» y el corte por empresa no lo ve.
 */
export function empresaQueCorresponde({ empresaId = null, clienteId = null } = {}, clientes = []) {
  if (clienteId) {
    const cliente = lista(clientes).find((c) => c && c.id === clienteId);
    return cliente?.empresaId || null;
  }
  return empresaId || null;
}

/**
 * ¿Se puede guardar un vuelo con esta empresa y este cliente? `null` si sí.
 *
 * ═══ POR QUÉ SE COMPRUEBA ALGO QUE «NO PUEDE PASAR» ═══
 *
 * Cuando hay cliente, la empresa sale de él; entonces parecería que no hay nada
 * que revisar. Pero el asiento guarda **las dos cosas**, y el informe lee
 * `empresaId` del asiento antes de mirar el cliente —así los vuelos viejos, sin
 * cliente, también caen en su empresa—. O sea: un asiento con la empresa en A y
 * el cliente de B se cuenta entero en A, y nada en pantalla lo delata.
 *
 * Por eso el desacuerdo se rechaza en vez de resolverse solo. Elegir cuál gana
 * en silencio es cómo un vuelo de una empresa aparece facturado en otra.
 *
 * ─── Y NO REVISA PERMISOS ───
 *
 * A propósito: elegir el cliente de un vuelo es parte de **registrar** el vuelo,
 * y ese permiso (`vuelos.escribir`) lo revisa quien escribe el vuelo, una vez.
 * Pedirlo acá otra vez sería una segunda definición de la misma regla, y la que
 * quede desactualizada va a ser la que nadie mire.
 */
export function revisarOperacionDelVuelo({ empresaId = null, clienteId = null } = {}, { clientes = [], empresas = [] } = {}) {
  if (!clienteId) {
    /* Sin cliente hace falta la empresa igual: es lo que hace que el corte por
       empresa vea el traslado propio. */
    if (!empresaId) return MOTIVO_CLIENTE.SIN_EMPRESA;
    return lista(empresas).some((e) => e.id === empresaId) ? null : MOTIVO_CLIENTE.EMPRESA_INEXISTENTE;
  }

  const cliente = lista(clientes).find((c) => c && c.id === clienteId);
  if (!cliente) return MOTIVO_CLIENTE.CLIENTE_INEXISTENTE;
  if (cliente.activo === false) return MOTIVO_CLIENTE.CLIENTE_DE_BAJA;
  /* Desacuerdo, sí; ausencia, no. Si no se dijo la empresa no hay nada que
     desmienta al cliente: **la empresa del vuelo es la suya**, y exigirla sería
     pedir un dato que ya está dicho en otro lado. */
  if (empresaId && cliente.empresaId !== empresaId) return MOTIVO_CLIENTE.EMPRESA_Y_CLIENTE_DISCREPAN;

  return null;
}

/**
 * El par ya resuelto, listo para guardar en el asiento: empresa y cliente que se
 * corresponden. Devuelve `{ ok, empresaId, clienteId, motivo }`.
 *
 * Existe para que ningún camino de la pantalla tenga que acordarse de derivar la
 * empresa del cliente. Si cada botón que registra un vuelo lo hiciera a mano,
 * habría tres derivaciones de la misma regla —el alta manual, el cierre del
 * vuelo y el botón del resumen— y alguna iba a quedar distinta.
 */
export function operacionDelVuelo(datos = {}, { clientes = [], empresas = [] } = {}) {
  const motivo = revisarOperacionDelVuelo(datos, { clientes, empresas });
  if (motivo) return { ok: false, empresaId: null, clienteId: null, motivo };

  const clienteId = datos.clienteId || null;
  return {
    ok: true,
    empresaId: empresaQueCorresponde({ empresaId: datos.empresaId, clienteId }, clientes),
    clienteId,
    motivo: null,
  };
}

/**
 * El asiento CON la operación adentro: para qué empresa y para qué cliente se
 * voló.
 *
 * Es el **único** lugar donde ese par entra en un asiento. Los tres caminos que
 * registran un vuelo —el alta manual de la bitácora, el botón del resumen y el
 * cierre del cronómetro— pasan por acá, y por eso no pueden derivar la empresa
 * cada uno a su manera: tres derivaciones de la misma regla es como una queda
 * distinta, y la que queda distinta no se nota.
 *
 * ═══ CON `ok: false` NO HAY ASIENTO ═══
 *
 * Devuelve `asiento: null` y no el asiento a medio armar. Un asiento con el
 * cliente de una empresa y la empresa de otra se cuenta entero en la empresa
 * equivocada —el informe lee `empresaId` antes de mirar el cliente— y nada en
 * pantalla lo delata. Preferimos que quien llama tenga que decidir qué hacer con
 * el motivo, a que exista un asiento que miente en silencio.
 */
export function conOperacion(asiento = {}, operacion = {}, { clientes = [], empresas = [] } = {}) {
  const resuelta = operacionDelVuelo(operacion, { clientes, empresas });
  if (!resuelta.ok) return { ok: false, asiento: null, motivo: resuelta.motivo };

  return {
    ok: true,
    asiento: { ...asiento, empresaId: resuelta.empresaId, clienteId: resuelta.clienteId },
    motivo: null,
  };
}
